import type { ChemicalStockUsageRow } from '../services/sprayDashboardService';

export type StockStatus    = 'sufficient' | 'low' | 'insufficient' | 'conflict';
export type VarianceStatus = 'ontarget' | 'overspend' | 'underspend';

export const DEFAULT_LOW_STOCK_PCT    = 0.8;
export const DEFAULT_VARIANCE_TOL_PCT = 0.05;

export interface EnrichedChemicalStockUsageRow extends ChemicalStockUsageRow {
  forecast:       number;
  coveragePct:    number;
  variancePct:    number;
  stockStatus:    StockStatus;
  varianceStatus: VarianceStatus;
}

/**
 * `stock: null` means the product's on-hand figure is in a unit-conflict
 * state (Slice 4) — there is no reliable number to compare against forecast.
 * Report 'conflict' and never divide/compare a null stock.
 */
export function stockStatus(stock: number | null, forecast: number, lowPct: number = DEFAULT_LOW_STOCK_PCT): StockStatus {
  if (stock == null) return 'conflict';
  if (forecast <= 0) return 'sufficient';
  if (stock >= forecast) return 'sufficient';
  if (stock >= forecast * lowPct) return 'low';
  return 'insufficient';
}

export function varianceStatus(actual: number, planned: number, tolPct: number = DEFAULT_VARIANCE_TOL_PCT): VarianceStatus {
  if (planned <= 0) return 'ontarget';
  const delta = (actual - planned) / planned;
  if (Math.abs(delta) <= tolPct) return 'ontarget';
  return delta > 0 ? 'overspend' : 'underspend';
}

export function enrichChemicalStockUsage(
  rows: ChemicalStockUsageRow[],
  opts: { lowPct?: number; tolPct?: number } = {},
): EnrichedChemicalStockUsageRow[] {
  const lowPct = opts.lowPct ?? DEFAULT_LOW_STOCK_PCT;
  const tolPct = opts.tolPct ?? DEFAULT_VARIANCE_TOL_PCT;
  return rows.map((r) => {
    const forecast    = r.draft + r.planned;
    // A unit-conflict row (stock: null) has no reliable on-hand figure to
    // compare against forecast — skip the division entirely rather than
    // treating a null stock as 0.
    const coveragePct = r.stock == null ? 0 : (forecast > 0 ? r.stock / forecast : 1);
    const variancePct = r.cPlanned > 0 ? (r.cActual - r.cPlanned) / r.cPlanned : 0;
    return {
      ...r,
      forecast,
      coveragePct,
      variancePct,
      stockStatus:    stockStatus(r.stock, forecast, lowPct),
      varianceStatus: varianceStatus(r.cActual, r.cPlanned, tolPct),
    };
  });
}

/**
 * Format a quantity with magnitude-aware decimal precision.
 *
 * Goal: NEVER display a non-zero value as "0". Small values get more decimal
 * places; sub-unit values can be down-converted (L→ml, kg→g) at the meter
 * level via selectDisplayUnit + convertToUnit before being passed here.
 *
 *   ≥ 1000  → locale-grouped integer ("1,500")
 *   ≥ 100   → integer
 *   ≥ 10    → 1 dp, trailing zeros stripped ("42" not "42.0")
 *   ≥ 1     → 2 dp, trailing zeros stripped ("5.07", "5" not "5.00")
 *   ≥ 0.01  → 2 dp, trailing zeros kept ("0.05")
 *   ≥ 0.001 → 3 dp ("0.005")
 *   ≥ 0.0001 → 4 dp
 *   < 0.0001 → "<0.0001" / ">-0.0001" sentinel (with a downconverted unit,
 *              this case should almost never fire, e.g., when values < 0.0001 ml)
 */
export function formatQty(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs === 0)       return '0';
  if (abs >= 1000)     return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 100)      return String(Math.round(n));
  if (abs >= 10)       return stripTrailingZeros(n.toFixed(1));
  if (abs >= 1)        return stripTrailingZeros(n.toFixed(2));
  if (abs >= 0.01)     return n.toFixed(2);
  if (abs >= 0.001)    return n.toFixed(3);
  if (abs >= 0.0001)   return n.toFixed(4);
  return n > 0 ? '<0.0001' : '>-0.0001';
}

function stripTrailingZeros(s: string): string {
  if (!s.includes('.')) return s;
  return s.replace(/\.?0+$/, '');
}

/**
 * Decide the display unit for a meter based on the largest value in that
 * meter. If the unit is downconvertible (L → ml, kg → g) AND the largest
 * non-zero value is < 1 (i.e. fractional), drop to the smaller unit so the
 * user sees whole or near-whole numbers rather than "0.05".
 *
 * Already-base units (g, ml) and null/unknown units are returned unchanged.
 *
 * @param values  numbers that share the same source unit
 * @param unit    the source unit
 */
export function selectDisplayUnit(values: number[], unit: string | null): string {
  if (unit !== 'L' && unit !== 'kg') return unit ?? '';
  const nonZero = values.filter(v => Number.isFinite(v) && v !== 0);
  if (nonZero.length === 0) return unit;
  const maxAbs = Math.max(...nonZero.map(Math.abs));
  // Downconvert when the largest non-zero value is still < 1 in the source
  // unit — i.e. would render with a leading "0." and either look tiny or
  // round to 0 at typical display precision.
  if (maxAbs < 1) return unit === 'L' ? 'ml' : 'g';
  return unit;
}

/**
 * Convert a value from its source unit to the display unit chosen by
 * selectDisplayUnit. Only L → ml and kg → g are supported (the downconvert
 * direction). Same-unit and unknown-unit cases return the value unchanged.
 */
export function convertToUnit(value: number, fromUnit: string | null, toUnit: string): number {
  if (fromUnit === toUnit) return value;
  if (fromUnit === 'L'  && toUnit === 'ml') return value * 1000;
  if (fromUnit === 'kg' && toUnit === 'g')  return value * 1000;
  return value;
}

export function formatPct(n: number): string {
  if (!Number.isFinite(n)) return '0%';
  const pct = Math.round(n * 100);
  if (pct === 0) return '0%';
  return (pct > 0 ? '+' : '') + pct + '%';
}
